/**
 * renderScreen.tsx — Screen rendering logic extracted from RetreatBridge.
 * Owns the 30+ case switch that maps AppScreen to React components.
 */
import { type ReactNode, type Dispatch } from "react";
import type { EngineSnapshot } from "@retreats/engine";
import type { UIState, UIAction, AppScreen } from "./types";
import type { BellId } from "../state/bellState";
import type { EditableRetreatSchedule } from "@retreat/core";
import type { IntentionRecord, IntentTag, WhisperScreen } from "@retreats/content";
import type { LiveScreenData } from "../hooks/useLiveScreenData";
import type { HomeData } from "../hooks/useHomeData";
import type { CompletionData } from "../hooks/useCompletionData";
import { fmtClock } from "../utils/scheduleUtils";
import type { SheetSession } from "../screens/LiveScheduleSheet";
import { findWhisper, traditions } from "@retreats/content";
import { STORAGE_KEYS, TIMING } from "../constants";
import { isE2E } from "../utils/env";
import type { RetreatSession } from "@retreat/core";
import type { WakeOffset } from "../constants";
import type { PlatformUpdate } from '../types/updates';
import {
  renderAuthScreens,
  renderDiscoveryScreens,
  renderScheduledRetreatScreens,
  renderLiveSessionScreens,
  renderPostSessionScreens,
  renderSettingsScreens,
  renderUtilityScreens,
  renderHomeScreen,
  type ScreenHelpers,
} from "./screenRenderers.tsx";


/**
 * Every handler key used in this file. TypeScript will error in RetreatBridge if
 * any entry is missing from the handlers object — no manual sync needed.
 */
export type Handlers = {
  handlePermissionsGranted: () => void;
  handlePermissionsSkip: () => void;
  handlePreparationContinue: () => void;
  handleReturningDismiss: () => void;
  handleReturningTakeover: () => void;
  handleReturningEnd: () => void;
  handleHearBell: () => void;
  handleEndConfirm: () => void;
  handleEndCancel: () => void;
  handleCompletionReturnHome: () => void;
  handleOpenLibrary: () => void;
  handleViewRetreatDetail: (s: EditableRetreatSchedule) => void;
  handleCloseLibrary: () => void;
  handleSelectRetreat: (s: EditableRetreatSchedule) => void;
  handleConfirmSchedule: (at: number, wakeOffset?: import("../constants").WakeOffset, generatedSchedule?: import("@retreat/core").EditableRetreatSchedule) => void;
  handleCancelScheduling: () => void;
  handleCancelScheduledRetreat: () => void;
  handleBeginScheduledRetreat: () => void;
  handleScheduleFromDetail: () => void;
  handleStartNow: (s: EditableRetreatSchedule) => void;
  handleIntentionComplete: (record: IntentionRecord) => void;
  handleIntentionDecline: () => void;
  handleOpenSettings: () => void;
  handleCloseSettings: () => void;
  handleCloseHistory: () => void;
  handleOpenBellLibrary: () => void;
  handleCloseBellLibrary: () => void;
  handleCloseBellPreview: () => void;
  handleOpenTrust: () => void;
  handleOpenTeaching: () => void;
  handlePlaySignal: (patternId: import("@retreat/core").BellPatternId) => void;
  getRetreatBellPatterns: (schedule: EditableRetreatSchedule) => import("@retreat/core").BellPatternId[];
};

type InstallHandlers = {
  handleInstallBridge: () => void;
  handleInstallSkip: () => void;
  handleInstallDone: () => void;
  handleInstallBack: () => void;
  isAppInstalled: boolean;
  swUpdatePending: boolean;
  applySwUpdate: () => void;
};

export type RenderContext = {
  uiState: UIState;
  snapshot: EngineSnapshot;
  handlers: Handlers;
  installHandlers: InstallHandlers;
  liveData: LiveScreenData;
  homeData: HomeData;
  completionData: CompletionData;
  sessions: RetreatSession[];
  defaultBellId: BellId;
  ttsEnabled: boolean;
  activeSchedule: EditableRetreatSchedule;
  demo: boolean;
  signOut: () => void;
  dispatchUI: Dispatch<UIAction>;
  handleSetDefaultBell: (id: BellId) => void;
  handleToggleTTS: (value: boolean) => void;
  // Settings v2
  bellsEnabled: boolean;
  whispersEnabled: boolean;
  preferredTradition: 'zen' | 'vipassana' | null;
  audioReflectionEnabled: boolean;
  handleToggleBells: (value: boolean) => void;
  handleToggleWhispers: (value: boolean) => void;
  handleSetTradition: (value: 'zen' | 'vipassana' | null) => void;
  handleToggleAudioReflection: (value: boolean) => void;
  handleEndGesture: () => void;
  handlePreviewBell: (id: BellId) => void;
  handleSaveReflection: (text: string, photo: File | null) => void;
  handleSkipReflection: () => void;
  handleCompletionWithReflection: () => void;
  handleContinueRetreat: () => void;
  demoRetreat: EditableRetreatSchedule;
  selectedHistorySession: RetreatSession | null;
  onSelectSession: (session: RetreatSession) => void;
  onCloseSessionDetail: () => void;
  intentLabels: string[];
  primaryIntentTag: IntentTag | null;
  wakeOffset: WakeOffset;
  handleChangeWakeOffset: (v: WakeOffset) => void;
  lastBellFiredAt: number | null;
  wave: string | null;
  isAdmin: boolean;
  userId: string;
  userEmail: string;
  /** Random seed generated once per page load — passed as instanceId to findWhisper. */
  whisperSeed: string;
  /** Pre-captured trace log — passed to FeedbackScreen as initialLogFile. */
  bugLogFile: File | null;
  updates: PlatformUpdate[];
  updatesUnreadCount: number;
  onMarkAllRead: () => void;
  /** True when AudioContext is suspended and auto-recovery has not yet succeeded. */
  audioSuspended: boolean;
  /** Resume AudioContext from a user gesture — passed to LiveScreen's recovery banner. */
  handleResumeAudio: () => void;
  /** User's personal schedules (soft-deleted already filtered out). */
  personalSchedules: import("../state/userScheduleRepository").UserSchedule[];
};

// Maps the app's user-facing tradition preference to the content package's TraditionId.
// 'zen' → Sōtō Zen (the app's primary Zen school; most retreat content is Sōtō).
// 'vipassana' → Insight Vipassana (Spirit Rock / IMS Barre lineage in the content set).
export const PREFERRED_TRADITION_TO_CONTENT_ID = {
  zen: 'soto_zen',
  vipassana: 'vipassana_insight',
} as const;

function buildDaySchedule(
  containers: { name: string; durationSeconds: number }[],
  elapsedSeconds: number,
  currentIdx: number,
): SheetSession[] {
  const retreatStartMs = Date.now() - elapsedSeconds * 1000;
  let accumStart = 0;
  return containers.map((c, i) => {
    const startLabel = fmtClock(retreatStartMs + accumStart * 1000);
    const durationMins = Math.round(c.durationSeconds / 60);
    const durationLabel = durationMins < 60
      ? `${durationMins} min`
      : `${Math.floor(durationMins / 60)}h${durationMins % 60 > 0 ? ` ${durationMins % 60}m` : ""}`;
    const containerEnd = accumStart + c.durationSeconds;
    let status: SheetSession["status"];
    if (elapsedSeconds >= containerEnd) status = "done";
    else if (elapsedSeconds >= accumStart) status = "current";
    else if (i === currentIdx + 1) status = "next";
    else status = "upcoming";
    accumStart = containerEnd;
    return { name: c.name, startLabel, durationLabel, status, durationSeconds: c.durationSeconds };
  });
}

/**
 * Scan localStorage for a reflection draft near a given timestamp.
 * The exact key is tried first; if absent, any draft key within the search
 * window is accepted (guards against a ~10s completedAt vs endedAt skew
 * that existed before the endedAt-keyed fix was deployed).
 */
function findReflectionDraft(endedAt: number): string | null {
  const draftPrefix = `${STORAGE_KEYS.REFLECTION_DRAFT}_`;
  try {
    const exactVal = localStorage.getItem(`${draftPrefix}${String(endedAt)}`);
    if (exactVal) return exactVal;
    for (const lsKey of Object.keys(localStorage)) {
      if (!lsKey.startsWith(draftPrefix)) continue;
      const ts = Number(lsKey.slice(draftPrefix.length));
      if (Number.isFinite(ts) && Math.abs(ts - endedAt) < TIMING.REFLECTION_DRAFT_SEARCH_WINDOW_MS) {
        const val = localStorage.getItem(lsKey);
        if (val) return val;
      }
    }
  } catch { /* quota or localStorage unavailable */ }
  return null;
}

/**
 * Look up the templateId for a given schedule by matching the schedule's id
 * against the manifest. Returns undefined for schedules with no template
 * or skipWizard=true.
 */
function getTemplateIdForSchedule(scheduleId: string): string | undefined {
  for (const tradition of traditions) {
    for (const entry of tradition.schedules) {
      if (entry.schedule.id === scheduleId && !entry.skipWizard) {
        return entry.templateId;
      }
    }
  }
  return undefined;
}

export function renderScreen(screen: AppScreen, ctx: RenderContext): ReactNode {
  const { whispersEnabled, preferredTradition } = ctx;

  const intent = ctx.primaryIntentTag ?? undefined;
  const tradId =
    preferredTradition != null
      ? PREFERRED_TRADITION_TO_CONTENT_ID[preferredTradition]
      : undefined;

  // Cache: one findWhisper call per screen.
  const _whisperCache = new Map<WhisperScreen, ReturnType<typeof findWhisper>>();
  const _resolved = (s: WhisperScreen) => {
    if (!whispersEnabled) return null;
    if (!_whisperCache.has(s)) {
      _whisperCache.set(
        s,
        findWhisper(s, { tradition: tradId, intent, seed: ctx.whisperSeed }),
      );
    }
    return _whisperCache.get(s) ?? null;
  };
  const fw = (s: WhisperScreen): string => _resolved(s)?.line ?? "";
  const fwOpt = (s: WhisperScreen): string | null => _resolved(s)?.line ?? null;
  const fwAttr = (s: WhisperScreen): string | undefined =>
    _resolved(s)?.attribution;

  const helpers: ScreenHelpers = {
    fw,
    fwOpt,
    fwAttr,
    buildDaySchedule,
    findReflectionDraft,
    getTemplateIdForSchedule,
  };

  if (isE2E()) {
    console.log(`[SCREEN] ${screen}`);
  }

  return (
    renderAuthScreens(screen, ctx) ??
    renderHomeScreen(screen, ctx, helpers) ??
    renderDiscoveryScreens(screen, ctx, helpers) ??
    renderScheduledRetreatScreens(screen, ctx, helpers) ??
    renderLiveSessionScreens(screen, ctx, helpers) ??
    renderPostSessionScreens(screen, ctx, helpers) ??
    renderSettingsScreens(screen, ctx) ??
    renderUtilityScreens(screen, ctx) ??
    null
  );
}
